import { inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { from, interval, map, Observable, of, Subject, Subscription, takeUntil, takeWhile, tap } from 'rxjs';
import { CommonService } from 'src/app/services/common/common.service';
import { PaymentService } from 'src/app/services/payment.service';

@Injectable()
export class BasePaymentService {
  private commonService = inject(CommonService);
  private authService = inject(AuthService);
  private paymentService = inject(PaymentService);

  private destroy$ = new Subject<void>();
  private timerSub: Subscription | null = null;
  remaining = 0;
  timedOut = false;

  start(seconds: number, displayEle, onTimeOut: () => void): void {
    // Cancel any countdown already in flight so repeated start() calls (e.g. when the
    // Checkout.com form is refreshed) do not stack multiple intervals decrementing the
    // same `remaining`, which would make the timer fire early and unpredictably.
    this.timerSub?.unsubscribe();

    this.remaining = seconds;
    this.timedOut = false;

    this.timerSub = interval(1000).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        const m = Math.floor(this.remaining / 60);
        const s = this.remaining % 60;
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        if (displayEle?.nativeElement) {
          displayEle.nativeElement.innerHTML = pad(m) + ':' + pad(s);
        }
        this.remaining--;
      }),
      takeWhile(() => this.remaining >= 0)
    ).subscribe({
      complete: () => {
        this.timedOut = true;
        onTimeOut();
      }
    })
  }

  dispose() {
    this.timerSub?.unsubscribe();
    this.timerSub = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  configureStoredCardsWatcher(userId: string): Observable<boolean> {
    const req = {
      parameter: "one_click_pay",
      condition: "one_click_pay_condition",
      feature: "one_click_pay",
      userId: userId,
    };

    return from(
      this.authService.getRemoteConfig(req).catch(() => null)
    ).pipe(
      // This stream only controls the remote feature toggle; card readiness is evaluated in PaymentPage.
      // Coupling to cashPurchaseService.storedCards$ can hide slide-to-buy on 4c routes where cards are sourced elsewhere.
      map((res: any) => {
        const remoteConfigData = res?.data;
        return !!(remoteConfigData?.one_click_pay ?? remoteConfigData ?? false);
      })
    );
  }

  fetchSyndicateAndShowTicket(syndicateId: string) {
    return this.paymentService
      .getSyndicateInfoWithProducts(syndicateId)
      .pipe(
        map((detail: any) => {
          const syndicateInfo = detail[0]?.data();
          if (syndicateInfo) {
            syndicateInfo.product = [];
          }
          const productDocs = detail[1].docs;
          for (let index = 0; index < productDocs.length; index++) {
            syndicateInfo?.product.push(productDocs[index]?.data());
          }
          if (!syndicateInfo) return of([]);
          syndicateInfo["cardType"] = "Managed";
          return of(syndicateInfo);
        })
      )
  }

  updateAddonDataForPaymilPayment(addOnData: Data[], currentNdc: string) {
    const path_id = "s69Msl086LrSKy09897r";
    const doc_data = { add_on: addOnData };
    const dv = { docId: currentNdc };
    const method = "update";

    this.authService
      .getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        dv,
        method,
      })
      .subscribe({
        error: () => { },
      });
  }

  private sanitizeToastText(text: string | null | undefined): string {
    if (typeof text !== 'string') {
      return '';
    }

    const withoutTags = text.replace(/<[^>]*>/g, ' ');
    const withoutEntities = withoutTags.replace(/&(lt|gt|amp|quot|#39|nbsp);/gi, ' ');
    return withoutEntities.replace(/\s+/g, ' ').trim();
  }

  showToast(
    code: string,
    message: string,
    type: 'Success' | 'Error'
  ) {
    this.authService.getConstantsMsg(code).then((translatedMsg) => {
      const safeMessage = this.sanitizeToastText(message) || 'Something went wrong!';
      const safeTranslatedMsg = translatedMsg ? {
        ...translatedMsg,
        message: this.sanitizeToastText(translatedMsg?.message) || safeMessage,
      } : null;
      const defaultMsg = {
        message: {
          line_1: safeMessage,
          line_2: "",
        },
        style: {
          icon: type === "Success" ? "tick circle success.png" : "tick circle error.svg",
          type: type,
        },
      };
      this.authService.showToastMessage(safeTranslatedMsg, defaultMsg);
    });
  }

}
